import React, { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Logo } from "@/components/Logo";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { User, Building2 } from "lucide-react";

export const AuthModal = ({ open, onOpenChange, onSuccess }) => {
  const { setUser } = useAuth();
  const [tab, setTab] = useState("login");
  const [loading, setLoading] = useState(false);
  const [login, setLogin] = useState({ email: "", password: "" });
  const [reg, setReg] = useState({ name: "", email: "", password: "", user_type: "particulier", company: "" });

  const doLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", login);
      setUser(data);
      toast.success(`Bienvenue ${data.name} !`);
      onOpenChange(false);
      onSuccess?.(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const doRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", reg);
      setUser(data);
      toast.success("Compte créé. Bienvenue chez ID-BAT PRO !");
      onOpenChange(false);
      onSuccess?.(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="auth-modal">
        <DialogHeader>
          <div className="flex justify-center mb-2">
            <Logo dark size={30} />
          </div>
          <DialogTitle className="text-center text-lg font-semibold text-[#0A0F1A]">
            Votre espace ID-BAT PRO
          </DialogTitle>
          <DialogDescription className="text-center text-sm text-[#5A677D]">
            Connectez-vous ou créez un compte pour accéder à vos analyses et à votre suivi de chantier.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="login" data-testid="auth-tab-login">Connexion</TabsTrigger>
            <TabsTrigger value="register" data-testid="auth-tab-register">Inscription</TabsTrigger>
          </TabsList>

          <TabsContent value="login">
            <form onSubmit={doLogin} className="space-y-4 pt-4">
              <div>
                <Label>Email</Label>
                <Input type="email" required data-testid="modal-auth-email-input"
                  value={login.email} onChange={(e) => setLogin({ ...login, email: e.target.value })} />
              </div>
              <div>
                <Label>Mot de passe</Label>
                <Input type="password" required data-testid="modal-auth-password-input"
                  value={login.password} onChange={(e) => setLogin({ ...login, password: e.target.value })} />
              </div>
              <Button type="submit" disabled={loading} data-testid="modal-auth-submit-btn"
                className="w-full bg-[#0D6EFD] hover:bg-[#0b5ed7] rounded-full h-11">
                {loading ? "Connexion..." : "Se connecter"}
              </Button>
            </form>
          </TabsContent>

          <TabsContent value="register">
            <form onSubmit={doRegister} className="space-y-4 pt-4">
              <div className="grid grid-cols-2 gap-2">
                {[
                  { v: "particulier", label: "Particulier", Icon: User },
                  { v: "professionnel", label: "Professionnel", Icon: Building2 },
                ].map(({ v, label, Icon }) => (
                  <button
                    type="button" key={v}
                    data-testid={`auth-type-${v}`}
                    onClick={() => setReg({ ...reg, user_type: v })}
                    className={`flex flex-col items-center gap-1 py-3 rounded-xl border-2 transition-all ${
                      reg.user_type === v ? "border-[#0D6EFD] bg-[#0D6EFD]/5" : "border-gray-200"
                    }`}
                  >
                    <Icon className={`w-5 h-5 ${reg.user_type === v ? "text-[#0D6EFD]" : "text-gray-400"}`} />
                    <span className="text-sm font-medium">{label}</span>
                  </button>
                ))}
              </div>
              <div>
                <Label>Nom complet</Label>
                <Input required data-testid="reg-name-input"
                  value={reg.name} onChange={(e) => setReg({ ...reg, name: e.target.value })} />
              </div>
              {reg.user_type === "professionnel" && (
                <div>
                  <Label>Entreprise</Label>
                  <Input data-testid="reg-company-input"
                    value={reg.company} onChange={(e) => setReg({ ...reg, company: e.target.value })} />
                </div>
              )}
              <div>
                <Label>Email</Label>
                <Input type="email" required data-testid="reg-email-input"
                  value={reg.email} onChange={(e) => setReg({ ...reg, email: e.target.value })} />
              </div>
              <div>
                <Label>Mot de passe (min. 6 caractères)</Label>
                <Input type="password" required data-testid="reg-password-input"
                  value={reg.password} onChange={(e) => setReg({ ...reg, password: e.target.value })} />
              </div>
              <Button type="submit" disabled={loading} data-testid="reg-submit-btn"
                className="w-full bg-[#0D6EFD] hover:bg-[#0b5ed7] rounded-full h-11">
                {loading ? "Création..." : "Créer mon compte"}
              </Button>
            </form>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
